import React, { useCallback, useEffect, useMemo, useState } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { Icons } from "../components/ui/icons";
import PageShell from "../components/page-shell";
import FilterField from "../components/mobile/filter-field";
import { DesktopOnly } from "../components/responsive";
import DataTable, { TableRowData } from "../components/data-table";
import { sitesTableColumns } from "../lib/table-columns";
import BarGraph from "../components/bar-graph";
import HorizontalBarGraph from "../components/horizontal-bar-graph";
import SearchInput from "../components/search-input";
import SelectInput from "../components/select-input";
import { Checkbox } from "../components/ui/checkbox";
import FormDrawer, { Field } from "../components/form-drawer";
import { siteFormFields } from "../lib/form-fields";
import { Edit } from "lucide-react";
import { RefreshToolbarButton } from "../components/refresh-toolbar-button";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getSites,
  getSiteStatistics,
  createSite,
  updateSite,
  getCustomers,
  CreateSitePayload,
  SiteStatistics,
  Customer,
  Site,
} from "../lib/api";
import { toast } from "sonner";
import { getBackendErrorMessage } from "../lib/utils";
import { format } from "date-fns";
import * as XLSX from "xlsx";

export type SitesFiltersState = {
  searchTerm: string;
  searchInput: string;
  hasOverdueDeliveries: boolean;
  selectedRegion: string;
  selectedStatus: string;
  currentPage?: number;
  pageSize?: number;
};

const Sites: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedRegion, setSelectedRegion] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [hasOverdueDeliveries, setHasOverdueDeliveries] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [formValues, setFormValues] = useState<{
    [key: string]: unknown;
  } | null>(null);
  const [dynamicFormFields, setDynamicFormFields] = useState<Field[]>(siteFormFields);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);

  const queryClient = useQueryClient();

  const SITES_FILTERS_STORAGE_KEY = "sitesFilters";

  const statusParam =
    selectedStatus && selectedStatus !== "all"
      ? (selectedStatus as "ACTIVE" | "INACTIVE")
      : undefined;
  const regionParam =
    selectedRegion && selectedRegion !== "all" ? selectedRegion : undefined;

  const sitesQuery = useQuery({
    queryKey: [
      "sites",
      {
        page: currentPage,
        limit: pageSize,
        search: searchTerm,
        hasOverdueDeliveries,
        status: statusParam,
        region: regionParam,
      },
    ],
    queryFn: async () =>
      getSites(
        currentPage,
        pageSize,
        searchTerm,
        hasOverdueDeliveries || undefined,
        statusParam,
        regionParam,
      ),
    placeholderData: keepPreviousData,
  });

  const statisticsQuery = useQuery<SiteStatistics>({
    queryKey: ["sites", "statistics"],
    queryFn: async () => getSiteStatistics(),
  });

  const customersQuery = useQuery({
    queryKey: ["customers", { page: 1, limit: 1000, search: "" }],
    queryFn: async () => getCustomers(1, 1000, ""),
  });

  const customerPhoneById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of customersQuery.data?.data ?? []) {
      if (c.phoneNumber) map.set(c.id, c.phoneNumber);
    }
    return map;
  }, [customersQuery.data]);

  const getCustomerPhone = useCallback(
    (site: Site) =>
      site.customer?.phoneNumber || customerPhoneById.get(site.customerId) || "",
    [customerPhoneById],
  );

  const sites = useMemo(() => {
    const data = sitesQuery.data?.data ?? [];
    return data.map((s) => ({
      id: s.id,
      site_name: s.name,
      customer_name: s.customer?.fullName || "-",
      customer_phone: getCustomerPhone(s) || "-",
      location: `${s.street}, ${s.ward}, ${s.district}, ${s.region}`,
      status: s.status,
      raw_data: s,
    })) as TableRowData[];
  }, [sitesQuery.data, getCustomerPhone]);

  const initialLoading = sitesQuery.isLoading || statisticsQuery.isLoading || customersQuery.isLoading;

  // Restore filters when returning from SiteDetails (state passed back via navigate)
  const hasRestoredFiltersRef = React.useRef(false);
  const isRestoringRef = React.useRef(false);
  useEffect(() => {
    if (hasRestoredFiltersRef.current) return;

    const fromNav = (location.state as { sitesFilters?: SitesFiltersState })?.sitesFilters;
    let next: SitesFiltersState | null = fromNav ?? null;

    if (!next) {
      try {
        const raw = sessionStorage.getItem(SITES_FILTERS_STORAGE_KEY);
        next = raw ? (JSON.parse(raw) as SitesFiltersState) : null;
      } catch {
        next = null;
      }
    }

    if (!next) return;
    hasRestoredFiltersRef.current = true;
    isRestoringRef.current = true;
    setSearchTerm(next.searchTerm ?? "");
    setSearchInput(next.searchInput ?? "");
    setHasOverdueDeliveries(next.hasOverdueDeliveries ?? false);
    setSelectedRegion(next.selectedRegion ?? "");
    setSelectedStatus(next.selectedStatus ?? "");
    if (typeof next.pageSize === "number" && Number.isFinite(next.pageSize)) {
      setPageSize(next.pageSize);
    }
    if (typeof next.currentPage === "number" && Number.isFinite(next.currentPage)) {
      setCurrentPage(next.currentPage);
    }
    // release the "skip reset" guard after this render
    queueMicrotask(() => {
      isRestoringRef.current = false;
    });
  }, [location.state]);

  // Reset pagination on region or overdue filter changes (search handled separately)
  useEffect(() => {
    if (isRestoringRef.current) return;
    setCurrentPage(1);
  }, [selectedRegion, selectedStatus, hasOverdueDeliveries]);

  // Debounce raw search input and apply to `searchTerm` after a short delay
  useEffect(() => {
    const handler = setTimeout(() => setSearchTerm(searchInput), 400);
    return () => clearTimeout(handler);
  }, [searchInput]);

  // When searchTerm changes (debounced above), perform a search without showing
  // the page loading state. If we're not already on page 1, set the ref and
  // reset page to 1 so the page effect will run suppressed; otherwise fetch
  // directly suppressed.
  useEffect(() => {
    if (isRestoringRef.current) return;
    if (currentPage !== 1) setCurrentPage(1);
  }, [searchTerm]);

  // Persist filters + pagination so sidebar navigation doesn't lose state.
  useEffect(() => {
    const payload: SitesFiltersState = {
      searchTerm,
      searchInput,
      hasOverdueDeliveries,
      selectedRegion,
      selectedStatus,
      currentPage,
      pageSize,
    };
    try {
      sessionStorage.setItem(SITES_FILTERS_STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // ignore storage errors
    }
  }, [searchTerm, searchInput, hasOverdueDeliveries, selectedRegion, selectedStatus, currentPage, pageSize]);

  // Update form field suggestions when customers load
  useEffect(() => {
    const customerData = customersQuery.data?.data ?? [];
    if (customerData.length === 0) return;
    const customerSuggestions = customerData.map((customer) => {
      const location = `${customer.ward ? customer.ward + ", " : ""}${customer.district}, ${customer.region}`;
      return `${customer.fullName} - ${customer.type} - ${location}`;
    });
    setDynamicFormFields(
      siteFormFields.map((field) => {
        if (field.key === "customer_name") {
          return {
            ...field,
            suggestions: customerSuggestions,
          } as typeof field;
        }
        return field;
      }),
    );
  }, [customersQuery.data]);

  const handleDownloadExcel = async () => {
    setDownloadingExcel(true);
    try {
      const total = Math.max(sitesQuery.data?.meta.total ?? 0, 1);
      const response = await getSites(
        1,
        total,
        searchTerm,
        hasOverdueDeliveries || undefined,
        statusParam,
        regionParam,
      );
      const data = response.data ?? [];
      if (data.length === 0) {
        toast.error("No sites to download");
        return;
      }

      const rows = data.map((site) => ({
        "Site Name": site.name ?? "",
        "Customer Name": site.customer?.fullName ?? "",
        "Customer Phone": getCustomerPhone(site),
        Street: site.street ?? "",
        Ward: site.ward ?? "",
        District: site.district ?? "",
        Region: site.region ?? "",
        Country: site.country ?? "",
        Status: site.status ?? "",
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Sites");
      XLSX.writeFile(wb, `Sites_${format(new Date(), "yyyy-MM-dd")}.xlsx`);
      toast.success("Sites Excel downloaded");
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to download Excel"));
    } finally {
      setDownloadingExcel(false);
    }
  };

  const actions = [
    {
      label: "View details",
      icon: Icons.Preview,
      onClick: (row: TableRowData) => {
        const raw = (row as any).raw_data;
        navigate("/site-details", {
          state: {
            id: raw.id,
            site_name: row?.site_name ?? "",
            fromSitesFilters: {
              searchTerm,
              searchInput,
              hasOverdueDeliveries,
              selectedRegion,
              selectedStatus,
            },
          },
        });
      },
    },
    {
      label: "Edit site",
      icon: Edit,
      onClick: (row: TableRowData) => {
        const raw = (row as any).raw_data;
        setEditingId(raw.id);

        // Format customer name for display in the same format as suggestions
        const customers = customersQuery.data?.data ?? [];
        const customer = customers.find((c) => c.id === raw.customerId);
        let customerNameDisplay = raw.customer?.fullName || "";
        if (customer) {
          const location = `${customer.ward ? customer.ward + ", " : ""}${customer.district}, ${customer.region}`;
          customerNameDisplay = `${customer.fullName} - ${customer.type} - ${location}`;
        }

        setFormValues({
          site_name: raw.name,
          site_street: raw.street,
          customer_name: customerNameDisplay,
          location: `${raw.ward ? raw.ward + ", " : ""}${raw.district}, ${raw.region}`,
          status: raw.status,
          customerId: raw.customerId,
        });
      },
    },
  ];

  const handleFormSubmit = async () => {
    if (!formValues) return;

    setIsSubmitting(true);

    // Find customer ID from selected customer name
    const customerName = (formValues.customer_name as string) || "";
    let customerId = formValues.customerId as string;

    // If customerId is not set, find it from the customer name
    if (!customerId && customerName) {
      const customers = customersQuery.data?.data ?? [];
      const selectedCustomer = customers.find((c) => {
        const location = `${c.ward ? c.ward + ", " : ""}${c.district}, ${c.region}`;
        const suggestionFormat = `${c.fullName} - ${c.type} - ${location}`;
        return suggestionFormat === customerName || c.fullName === customerName;
      });
      customerId = selectedCustomer?.id || "";
    }

    if (!customerId) {
      toast.error("Customer ID is required");
      setIsSubmitting(false);
      return;
    }

    // Parse location "Ward, District, Region"
    const locationStr = (formValues.location as string) || "";
    const locationParts = locationStr.split(",").map((s) => s.trim());
    const region =
      locationParts.length > 0 ? locationParts[locationParts.length - 1] : "";
    const district =
      locationParts.length > 1 ? locationParts[locationParts.length - 2] : "";
    const ward =
      locationParts.length > 2 ? locationParts[locationParts.length - 3] : "";

    // Construct payload
    const payload: CreateSitePayload = {
      customerId: customerId,
      name: formValues.site_name as string,
      street: formValues.site_street as string,
      ward: ward || "",
      district: district || "",
      region: region || "",
      country: "Tanzania",
      status: (formValues.status as "ACTIVE" | "INACTIVE") || "ACTIVE",
    };

    try {
      if (editingId) {
        await updateSite(editingId, payload);
      } else {
        await createSite(payload);
      }
      setFormValues(null);
      setEditingId(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["sites"] }),
        queryClient.invalidateQueries({ queryKey: ["sites", "statistics"] }),
      ]);
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to save site"));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate percentages for BarGraph
  const totalRegionsCount =
    statisticsQuery.data?.byRegion.reduce((acc, curr) => acc + curr.count, 0) || 1;
  const regionMetrics =
    (statisticsQuery.data?.byRegion.map((r) => ({
      label: r.region,
      count: r.count,
      percentage: Math.round((r.count / totalRegionsCount) * 100),
      avgDays: 0,
    })) || []).sort((a, b) => b.count - a.count);

  // Data for HorizontalBarGraph (Using top customers)
  const transactionalData =
    statisticsQuery.data?.topCustomers.slice(0, 5).map((tc) => ({
      category: tc.customerName,
      gap: 0.2,
      values: [
        {
          label: "Sites",
          value: tc.siteCount,
          fillColor: "#bce3ff",
        },
      ],
    })) || [];

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["sites"] }),
      queryClient.invalidateQueries({ queryKey: ["sites", "statistics"] }),
    ]);

  const openNewSite = () => setFormValues({});

  const resetFilters = () => {
    setHasOverdueDeliveries(false);
    setSelectedStatus("");
    setSelectedRegion("");
  };

  const activeFilterCount =
    (hasOverdueDeliveries ? 1 : 0) +
    (selectedStatus && selectedStatus !== "all" ? 1 : 0) +
    (selectedRegion && selectedRegion !== "all" ? 1 : 0);

  const downloadDisabled =
    downloadingExcel || (sitesQuery.data?.meta.total ?? 0) === 0;

  const overdueCheckbox = (
    <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
      <Checkbox
        id="sites-overdue-deliveries"
        checked={hasOverdueDeliveries}
        onCheckedChange={(checked) => {
          setHasOverdueDeliveries(checked === true);
        }}
        aria-label="Overdue deliveries only"
      />
      Overdue deliveries only
    </label>
  );

  const statusSelect = (
    <SelectInput
      value={selectedStatus}
      options={[
        { label: "All Statuses", value: "all" },
        { label: "Active", value: "ACTIVE" },
        { label: "Inactive", value: "INACTIVE" },
      ]}
      placeholder="Status"
      onChange={(value) => setSelectedStatus(value)}
    />
  );

  const regionSelect = (
    <SelectInput
      value={selectedRegion}
      options={[
        { label: "All Regions", value: "all" },
        ...(statisticsQuery.data?.byRegion
          .filter((r) => Boolean(r.region?.trim()))
          .map((r) => ({
            label: r.region,
            value: r.region,
          })) || []),
      ]}
      placeholder={"Region"}
      onChange={(value) => setSelectedRegion(value)}
    />
  );

  return (
    <>
      <FormDrawer
        title={editingId ? "Edit Site" : "Add New Site"}
        buttonText={editingId ? "Update Site" : "Add Site"}
        formValues={formValues}
        setFormValues={setFormValues}
        fields={dynamicFormFields}
        handleSubmit={handleFormSubmit}
        isSubmitting={isSubmitting}
        onClose={() => {
          setFormValues(null);
          setEditingId(null);
        }}
      />
      <PageShell
        title="Sites"
        description="Keep an organized record of all sites and their rental activities."
        loading={initialLoading}
        toolbar={
          <>
            <RefreshToolbarButton
              isRefreshing={sitesQuery.isFetching || statisticsQuery.isFetching}
              onRefresh={refresh}
            />
            <Button variant="default" className="h-9" onClick={openNewSite}>
              <Icons.Plus className="h-4 w-4 mr-2" />
              New Site
            </Button>
          </>
        }
        stats={[
          {
            label: "Total Sites",
            value: statisticsQuery.data?.totalSites.toString() || "0",
            icon: <Icons.Building className="w-5 h-5 text-primary-600" />,
          },
          {
            label: "Active",
            value: statisticsQuery.data?.byStatus.active.toString() || "0",
            icon: <Icons.Building className="w-5 h-5 text-primary-600" />,
          },
          {
            label: "Not Active",
            value: statisticsQuery.data?.byStatus.inactive.toString() || "0",
            icon: <Icons.Building className="w-5 h-5 text-primary-600" />,
          },
        ]}
        insights={
          <div className="grid grid-cols-1 lg:grid-cols-[1fr,400px] gap-4">
            <HorizontalBarGraph
              graphTitle="Top Customers by Site Count"
              data={transactionalData.slice(0, 3)}
            />
            <BarGraph graphTitle="Sites by Regions" metrics={regionMetrics.slice(0, 3)} />
          </div>
        }
        mobile={{
          onRefresh: refresh,
          primaryAction: { label: "New Site", icon: Icons.Plus, onClick: openNewSite },
          actions: [
            {
              label: downloadingExcel ? "Downloading..." : "Download Excel",
              icon: Icons.Download,
              onClick: () => void handleDownloadExcel(),
              disabled: downloadDisabled,
            },
          ],
          filters: (
            <>
              <FilterField label="Deliveries">{overdueCheckbox}</FilterField>
              <FilterField label="Status">{statusSelect}</FilterField>
              <FilterField label="Region">{regionSelect}</FilterField>
            </>
          ),
          activeFilterCount,
          onResetFilters: resetFilters,
        }}
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <SearchInput
            searchTerm={searchInput}
            onSearchChange={(value) => setSearchInput(value)}
            placeholder="Search sites..."
          />
          <DesktopOnly>
            <div className="flex items-center gap-3">
              {overdueCheckbox}
              {statusSelect}
              {regionSelect}
              <Button
                variant="outline"
                className="h-9"
                disabled={downloadDisabled}
                onClick={() => void handleDownloadExcel()}
              >
                <Icons.Download className="h-4 w-4 mr-2" />
                {downloadingExcel ? "Downloading..." : "Download Excel"}
              </Button>
            </div>
          </DesktopOnly>
        </div>
        <DataTable
          columns={sitesTableColumns}
          data={sites}
          actions={actions}
          loading={sitesQuery.isFetching}
          pagination={{
            page: currentPage,
            pageSize,
            totalItems: sitesQuery.data?.meta.total ?? 0,
            totalPages: sitesQuery.data?.meta.totalPages ?? 1,
            onPageChange: (page) => {
              setCurrentPage(page);
            },
            onPageSizeChange: (size) => {
              setPageSize(size);
            },
          }}
        />
      </PageShell>
    </>
  );
};

export default Sites;
